import { alertVariants } from "@visiblethread/react-components";
import { PromptTypes } from "@visiblethread/visi";
import localhost, { downloadFileFromResponse } from "apis/localhost";
import { getPathToTableRow } from "components/ExcelTable";
import { UPLOAD_ENTITY_TYPES } from "constants/documentUploads";
import { getIsCategory, PROMPT_TYPES } from "layout/prompts/utils";
import { findIndex, findKey, get, last, pick } from "lodash";
import { showErrorModal } from "slices/app/actions/error";
import {
  addToJobQueue,
  disableNavBarAndSideBar,
  pageLoading,
  removeFromJobQueue,
  showGlobalAlert,
} from "slices/app/appSlice";
import { NEW_ID_PREFIX } from "utils/dictionary";

import {
  addChildByPath,
  loadPromptData,
  setPromptEditorIsNotEditing,
  setPromptEditorViewMode,
  setVisiPrompts,
  updateAllItems,
} from "./promptsSlice";
import { selectPromptsData } from "./selectors";

const mapTypesToFrontend = ({
  categories,
  users,
  isLockedBy,
  lockedByAnotherUser,
}) => {
  return {
    categories: categories.map((category) => ({
      ...category,
      categoryId: category.id,
      categoryType: category.categoryType,
      prompts: category.prompts.map((prompt, idx) => ({
        ...prompt,
        id: `prompt-${category.id}-${idx}`,
        promptType: PROMPT_TYPES[prompt.promptType] || prompt.promptType,
        newlyCreated: false,
      })),
      error: "",
      newlyCreated: false,
      collapsed: true,
      displayContexts: category.displayContexts || [],
    })),
    users: users || undefined,
    isLockedBy,
    lockedByAnotherUser,
  };
};

export const fetchAiPrompts = () => async (dispatch) => {
  dispatch(pageLoading(true));

  try {
    const response = await localhost.get("/prompt-categories");
    dispatch(loadPromptData(mapTypesToFrontend(response.data)));
  } catch (error) {
    dispatch(showErrorModal(error));
    dispatch(loadPromptData([]));
  } finally {
    dispatch(pageLoading(false));
  }
};

export const lockAiPrompts = () => async (dispatch) => {
  try {
    await localhost.get("/prompt-categories/lock");
  } catch (error) {
    if (error.status === 423) {
      dispatch(setPromptEditorViewMode());
      dispatch(setPromptEditorIsNotEditing());
      dispatch(disableNavBarAndSideBar(false));
      dispatch(fetchAiPrompts());
    } else {
      dispatch(showErrorModal(error));
    }
  }
};

export const unlockAiPrompts = () => async (dispatch) => {
  try {
    await localhost.get("/prompt-categories/unlock");
  } catch (error) {
    dispatch(showErrorModal(error));
  }
};

const categoryFields = [
  "canEdit",
  "categoryType",
  "description",
  "displayContexts",
  "name",
  "order",
  "shares",
];

const getPayloadShape = (categories = []) => {
  return categories.map((category) => {
    const { isNew } = category;
    const shape = pick(category, categoryFields);
    const id = !isNew ? category.id : undefined;
    const prompts = category.prompts.map((p, idx) => ({
      order: idx + 1,
      shortcut: p.shortcut,
      prompt: p.prompt,
      promptType: findKey(PROMPT_TYPES, (val) => val === p.promptType),
    }));
    return { ...shape, id, prompts };
  });
};

export const saveAiPrompts = () => async (dispatch, getState) => {
  dispatch(pageLoading(true));
  const state = getState();
  const categories = selectPromptsData(state);

  try {
    const payload = getPayloadShape(categories);

    const response = await localhost.post("/prompt-categories", payload);
    const formattedData = mapTypesToFrontend({ categories: response.data });
    dispatch(updateAllItems(formattedData.categories));
    dispatch(unlockAiPrompts());
  } catch (error) {
    dispatch(showErrorModal(error));
  } finally {
    dispatch(pageLoading(false));
  }
};

export const getVisiPrompts = (entityType) => async (dispatch) => {
  dispatch(pageLoading(true));
  const mapPromptTypes = (data) => {
    return data.map((section) => ({
      ...section,
      prompts: section.prompts.map((prompt) => ({
        ...prompt,
        type:
          prompt.type === "default"
            ? PromptTypes.default
            : PromptTypes.selection,
      })),
    }));
  };
  const getDisplayContext = (type) => {
    switch (type) {
      case UPLOAD_ENTITY_TYPES.OPPORTUNITY:
        return "TRACKED_OPPORTUNITY_RESEARCH";
      case UPLOAD_ENTITY_TYPES.MISC_ANALYSIS_RESEARCH:
      case UPLOAD_ENTITY_TYPES.MISC_ANALYSIS_PROPOSAL_WRITING:
        return "MISC_ANALYSIS_WRITING_EDITING";
      default:
        return "PROPOSALS_WRITING_EDITING";
    }
  };

  try {
    const response = await localhost.get("/prompt-categories/visi", {
      params: {
        displayContext: getDisplayContext(entityType),
      },
    });

    dispatch(setVisiPrompts(mapPromptTypes(response.data)));
  } catch (error) {
    dispatch(showErrorModal(error));
    dispatch(setVisiPrompts([]));
  } finally {
    dispatch(pageLoading(false));
  }
};

export const createEmptyPrompt = () => {
  return {
    order: -1,
    promptType: PROMPT_TYPES.default,
    shortcut: "",
    caseSensitive: false,
    prompt: "",
    isNew: true,
  };
};

export const addEmptyPromptByRow = (row) => (dispatch, getState) => {
  const state = getState();
  const prompts = selectPromptsData(state);
  const isCategoryRow = getIsCategory(row.original);

  const prompt = createEmptyPrompt();
  prompt.id = `${NEW_ID_PREFIX}-${Date.now()}`;

  // If the Row is a category, then we just need to add a blank
  // Prompt to the first index of the Category's array of Prompts.
  if (isCategoryRow) {
    const categoryId = row.original.id;

    prompt.categoryId = categoryId;
    const categoryIdx = findIndex(prompts, ["id", categoryId]);

    dispatch(
      addChildByPath({
        pathToChild: [categoryIdx, "prompts"],
        child: prompt,
        index: 0,
      })
    );

    // check the Row wasn't collapsed; if it is, expand
    // it so we can edit the newly created Row.
    const isCategoryExpanded = row.getIsExpanded();
    if (!isCategoryExpanded) {
      row.toggleExpanded();
    }

    return prompt;
  }

  // Get the full path to this particular row (ie, Prompt);
  const pathToEdit = getPathToTableRow(row, { subRowKey: "prompts" });

  // Then figure out the index from which we append a new Prompt
  // Plus the path to the prompts[] for the specific category.
  const index = last(pathToEdit);
  const pathToChild = pathToEdit.slice(0, -1);

  const pathToCategory = pathToChild.slice(0, -1);
  const category = get(prompts, pathToCategory);

  prompt.categoryId = category?.id;

  dispatch(addChildByPath({ pathToChild, child: prompt, index: index + 1 }));

  return prompt;
};

export const importPromptsFromCSV = (file, importMode) => async (dispatch) => {
  dispatch(addToJobQueue("importPromptsFromCSV"));
  const formData = new FormData();
  formData.append("importedFile", file, file.name);

  try {
    const response = await localhost.post(
      "/prompt-categories/import/csv",
      formData,
      { params: { importMode } }
    );
    await dispatch(fetchAiPrompts());
    const { importedCount, skippedCount } = response.data;
    const alertText =
      skippedCount > 0
        ? `${importedCount} row(s) imported, ${skippedCount} row(s) skipped.`
        : `All ${importedCount} row(s) imported successfully.`;
    dispatch(
      showGlobalAlert({
        variant:
          skippedCount > 0 ? alertVariants.warning : alertVariants.success,
        text: alertText,
      })
    );
  } catch (error) {
    if (error.response?.status === 400) {
      dispatch(
        showGlobalAlert({
          variant: alertVariants.danger,
          text: error.response.data,
        })
      );
    } else {
      dispatch(showErrorModal(error));
    }
  } finally {
    dispatch(removeFromJobQueue("importPromptsFromCSV"));
  }
};

export const exportPromptsToCSV = () => async (dispatch) => {
  dispatch(addToJobQueue("exportPromptsToCSV"));

  try {
    const response = await localhost.get("/prompt-categories/export", {
      responseType: "blob",
    });
    downloadFileFromResponse(response.data, "Prompt_Library.csv");
  } catch (error) {
    dispatch(showErrorModal(error));
  } finally {
    dispatch(removeFromJobQueue("exportPromptsToCSV"));
  }
};
